import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Calculator, Clock, Users, DollarSign, Calendar } from 'lucide-react';
import { calculateEstimate, UNIT_TYPES, ONSITE_TIME } from '../utils/EstimateCalculationEngine';

export default function EstimateForm({ onSave, onCancel, initialData, leadData }) {
  const [formData, setFormData] = useState(initialData || leadData || {
    client_name: '',
    site_address: '',
    building_type: 'apartment',
    unit_type: '2b_2b',
    num_units: 10,
    avg_unit_sqft: 1000,
    visits_initial: true,
    visits_final: true,
    complexity_finish_level: 'standard',
    complexity_mess_level: 'normal',
    crew_size_suggested: 4,
    hourly_wage: 20,
    burden_factor: 1.25,
    markup_factor: 1.4,
    status: 'draft',
    notes: ''
  });

  const [calculations, setCalculations] = useState(null);

  useEffect(() => {
    const result = calculateEstimate(formData);
    setCalculations(result);
  }, [formData]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = () => {
    if (!calculations) return;
    onSave({
      ...formData,
      total_hours: calculations.totalHours,
      days_needed: calculations.daysNeeded,
      labour_cost: calculations.labourCost,
      quote_price: calculations.quotePrice,
      price_per_unit: calculations.pricePerUnit,
      price_per_sqft: calculations.pricePerSqft
    });
  };

  // Get on-site time display for current unit type and crew
  const getOnsiteTimeDisplay = () => {
    const times = ONSITE_TIME[formData.unit_type] || ONSITE_TIME['2b_2b'];
    const crew = formData.crew_size_suggested;
    let timeRange;
    if (crew <= 2) timeRange = times.crew2;
    else if (crew === 3) timeRange = times.crew3;
    else timeRange = times.crew4;
    return `${timeRange[0]}-${timeRange[1]} hrs`;
  };

  return (
    <Card className="border-0 shadow-none">
      <CardHeader className="pb-4">
        <CardTitle className="text-2xl">
          {initialData ? 'Edit Estimate' : 'New Estimate'}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Basic Info */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Client Name *</Label>
            <Input
              value={formData.client_name}
              onChange={(e) => handleInputChange('client_name', e.target.value)}
              placeholder="Client or company name"
            />
          </div>
          <div className="space-y-2">
            <Label>Site Address</Label>
            <Input
              value={formData.site_address}
              onChange={(e) => handleInputChange('site_address', e.target.value)}
              placeholder="Job site address"
            />
          </div>
        </div>

        {/* Unit Type Selection - MASTER TABLE */}
        <div className="border-t pt-6">
          <h3 className="text-lg font-semibold mb-4">Unit Type (Master Table)</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Unit Type *</Label>
              <Select value={formData.unit_type} onValueChange={(v) => {
                const unitData = UNIT_TYPES[v];
                handleInputChange('unit_type', v);
                if (unitData) handleInputChange('avg_unit_sqft', unitData.sqft);
              }}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(UNIT_TYPES).map(([key, value]) => (
                    <SelectItem key={key} value={key}>
                      {value.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Number of Units *</Label>
              <Input
                type="number"
                value={formData.num_units}
                onChange={(e) => handleInputChange('num_units', parseFloat(e.target.value) || 0)}
              />
            </div>
            <div className="space-y-2">
              <Label>Avg Sq Ft (Override)</Label>
              <Input
                type="number"
                value={formData.avg_unit_sqft}
                onChange={(e) => handleInputChange('avg_unit_sqft', parseFloat(e.target.value) || 0)}
              />
            </div>
          </div>

          {/* Labour Hours Reference */}
          {calculations && (
            <div className="mt-4 p-4 bg-blue-50 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <Clock className="w-4 h-4 text-blue-600" />
                <span className="font-semibold text-blue-900">Labour Hours Reference</span>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <p className="text-gray-600">Unit Type</p>
                  <p className="font-bold">{calculations.unitTypeLabel}</p>
                </div>
                <div>
                  <p className="text-gray-600">Labour Hours (Total)</p>
                  <p className="font-bold">{calculations.labourHoursRange} hrs/unit</p>
                </div>
                <div>
                  <p className="text-gray-600">On-Site Time ({formData.crew_size_suggested} cleaners)</p>
                  <p className="font-bold">{getOnsiteTimeDisplay()}/unit</p>
                </div>
                <div>
                  <p className="text-gray-600">Approx Sq Ft</p>
                  <p className="font-bold">~{formData.avg_unit_sqft}</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Scope & Complexity */}
        <div className="border-t pt-6">
          <h3 className="text-lg font-semibold mb-4">Scope & Complexity</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label>Initial Clean</Label>
              <Select value={formData.visits_initial ? 'yes' : 'no'} onValueChange={(v) => handleInputChange('visits_initial', v === 'yes')}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="yes">Yes</SelectItem>
                  <SelectItem value="no">No</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Final Clean</Label>
              <Select value={formData.visits_final ? 'yes' : 'no'} onValueChange={(v) => handleInputChange('visits_final', v === 'yes')}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="yes">Yes</SelectItem>
                  <SelectItem value="no">No</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Finish Level</Label>
              <Select value={formData.complexity_finish_level} onValueChange={(v) => handleInputChange('complexity_finish_level', v)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="standard">Standard (1.0x)</SelectItem>
                  <SelectItem value="high_end">High End (1.15x)</SelectItem>
                  <SelectItem value="luxury">Luxury (1.25x)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Mess Level</Label>
              <Select value={formData.complexity_mess_level} onValueChange={(v) => handleInputChange('complexity_mess_level', v)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="clean">Clean (0.9x)</SelectItem>
                  <SelectItem value="normal">Normal (1.0x)</SelectItem>
                  <SelectItem value="messy">Messy (1.2x)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Crew & Pricing */}
        <div className="border-t pt-6">
          <h3 className="text-lg font-semibold mb-4">Crew & Pricing</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label>Crew Size</Label>
              <Select value={formData.crew_size_suggested?.toString()} onValueChange={(v) => handleInputChange('crew_size_suggested', parseInt(v))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="2">2 Cleaners</SelectItem>
                  <SelectItem value="3">3 Cleaners</SelectItem>
                  <SelectItem value="4">4 Cleaners</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Hourly Wage ($)</Label>
              <Input
                type="number"
                step="0.5"
                value={formData.hourly_wage}
                onChange={(e) => handleInputChange('hourly_wage', parseFloat(e.target.value) || 0)}
              />
            </div>
            <div className="space-y-2">
              <Label>Burden Factor</Label>
              <Input
                type="number"
                step="0.05"
                value={formData.burden_factor}
                onChange={(e) => handleInputChange('burden_factor', parseFloat(e.target.value) || 1)}
              />
            </div>
            <div className="space-y-2">
              <Label>Markup (%)</Label>
              <Input
                type="number"
                step="5"
                value={Math.round((formData.markup_factor - 1) * 100)}
                onChange={(e) => handleInputChange('markup_factor', 1 + (parseFloat(e.target.value) || 0) / 100)}
              />
            </div>
          </div>
        </div>

        {/* Calculated Estimate Preview */}
        {calculations && (
          <div className="border-t pt-6">
            <div className="flex items-center gap-2 mb-2">
              <input 
                type="checkbox" 
                id="show-preview" 
                defaultChecked 
                className="w-4 h-4"
              />
              <Label htmlFor="show-preview" className="text-lg font-semibold flex items-center gap-2 cursor-pointer">
                <Calculator className="w-5 h-5" />
                Estimate Preview
              </Label>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <div className="bg-blue-50 p-4 rounded-lg">
                <div className="flex items-center gap-2 mb-1">
                  <Clock className="w-4 h-4 text-blue-600" />
                  <p className="text-xs text-gray-600">Total Hours</p>
                </div>
                <p className="text-xl font-bold text-blue-900">{calculations.totalHours}h</p>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg">
                <div className="flex items-center gap-2 mb-1">
                  <Calendar className="w-4 h-4 text-purple-600" />
                  <p className="text-xs text-gray-600">Days Needed</p>
                </div>
                <p className="text-xl font-bold text-purple-900">{calculations.daysNeeded}</p>
              </div>
              <div className="bg-amber-50 p-4 rounded-lg">
                <div className="flex items-center gap-2 mb-1">
                  <Users className="w-4 h-4 text-amber-600" />
                  <p className="text-xs text-gray-600">Labour Cost</p>
                </div>
                <p className="text-xl font-bold text-amber-900">${calculations.labourCost.toLocaleString()}</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg">
                <div className="flex items-center gap-2 mb-1">
                  <DollarSign className="w-4 h-4 text-green-600" />
                  <p className="text-xs text-gray-600">Quote Price</p>
                </div>
                <p className="text-xl font-bold text-green-900">${calculations.quotePrice.toLocaleString()}</p>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="flex items-center gap-2 mb-1">
                  <DollarSign className="w-4 h-4 text-gray-600" />
                  <p className="text-xs text-gray-600">Per Unit</p>
                </div>
                <p className="text-xl font-bold text-gray-900">${calculations.pricePerUnit.toLocaleString()}</p>
              </div>
            </div>
            <div className="mt-3 flex gap-4 text-sm text-gray-600">
              <span>Complexity: {calculations.complexityMultiplier}x</span>
              <span>•</span>
              <span>$/sqft: ${calculations.pricePerSqft}</span>
            </div>
          </div>
        )}

        {/* Notes */}
        <div className="border-t pt-6">
          <Label>Notes</Label>
          <Textarea
            value={formData.notes}
            onChange={(e) => handleInputChange('notes', e.target.value)}
            placeholder="Additional notes or special requirements"
            rows={3}
            className="mt-2"
          />
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-4 border-t">
          <Button variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit} 
            disabled={!formData.client_name || !formData.num_units}
            className="bg-green-600 hover:bg-green-700"
          >
            Save Estimate
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
